import { supabase } from './supabase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

export async function apiRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params } = options;

  // Build URL with query params
  let url = `${API_URL}/api/v1${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Get auth token
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(
      data.error || 'An error occurred',
      data.code || 'UNKNOWN_ERROR',
      response.status,
      data.details
    );
  }

  return data;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// API helper functions
export const api = {
  // Auth
  auth: {
    register: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string }) =>
      apiRequest<{ data: { user: any; session: any } }>('/auth/register', { method: 'POST', body: data }),
    login: (data: { email: string; password: string }) =>
      apiRequest<{ data: { user: any; session: any } }>('/auth/login', { method: 'POST', body: data }),
    logout: () => apiRequest('/auth/logout', { method: 'POST' }),
    me: () => apiRequest<{ data: any }>('/auth/me'),
  },

  // Courses
  courses: {
    list: () => apiRequest<{ data: any[] }>('/courses'),
    get: (slug: string) => apiRequest<{ data: any }>(`/courses/${slug}`),
  },

  // Tee Times
  teeTimes: {
    list: (params?: Record<string, any>) => apiRequest<{ data: any[] }>('/tee-times', { params }),
    get: (id: string) => apiRequest<{ data: any }>(`/tee-times/${id}`),
    getByDateAndCourse: (courseId: string, date: string) =>
      apiRequest<{ data: any[] }>(`/tee-times/course/${courseId}/date/${date}`),
  },

  // Bookings
  bookings: {
    create: (data: any) => apiRequest<{ data: any }>('/bookings', { method: 'POST', body: data }),
    list: (params?: Record<string, any>) => apiRequest<{ data: any[]; pagination: any }>('/users/me/bookings', { params }),
    get: (id: string) => apiRequest<{ data: any }>(`/bookings/${id}`),
    getByConfirmation: (code: string) => apiRequest<{ data: any }>(`/bookings/confirmation/${code}`),
    cancel: (id: string, reason?: string) =>
      apiRequest<{ data: any }>(`/bookings/${id}/cancel`, { method: 'POST', body: { reason } }),
  },

  // Products
  products: {
    list: (params?: Record<string, any>) => apiRequest<{ data: any[]; pagination: any }>('/products', { params }),
    get: (id: string) => apiRequest<{ data: any }>(`/products/${id}`),
    featured: (courseId?: string) => apiRequest<{ data: any[] }>('/products/featured', { params: { courseId } }),
  },

  // Orders
  orders: {
    create: (data: any) => apiRequest<{ data: any }>('/orders', { method: 'POST', body: data }),
    list: (params?: Record<string, any>) => apiRequest<{ data: any[]; pagination: any }>('/users/me/orders', { params }),
    get: (id: string) => apiRequest<{ data: any }>(`/orders/${id}`),
  },

  // Payments
  payments: {
    create: (data: any) => apiRequest<{ data: any }>('/payments', { method: 'POST', body: data }),
  },

  // Promo Codes
  promoCodes: {
    validate: (data: { code: string; applicableTo: 'bookings' | 'merchandise'; courseId?: string; amount: number }) =>
      apiRequest<{ data: any }>('/promo-codes/validate', { method: 'POST', body: data }),
  },

  // Loyalty
  loyalty: {
    account: () => apiRequest<{ data: any }>('/loyalty/account'),
    transactions: (params?: Record<string, any>) => apiRequest<{ data: any[]; pagination: any }>('/loyalty/transactions', { params }),
    rewards: () => apiRequest<{ data: any[] }>('/loyalty/rewards'),
    redeem: (data: any) => apiRequest<{ data: any }>('/loyalty/redeem', { method: 'POST', body: data }),
    referral: (code: string) => apiRequest<{ data: any }>('/loyalty/referral', { method: 'POST', body: { code } }),
  },

  // User
  user: {
    update: (data: any) => apiRequest<{ data: any }>('/users/me', { method: 'PATCH', body: data }),
  },

  // Waitlist
  waitlist: {
    create: (data: any) => apiRequest<{ data: any }>('/waitlist', { method: 'POST', body: data }),
    list: () => apiRequest<{ data: any[] }>('/waitlist'),
    cancel: (id: string) => apiRequest<{ data: any }>(`/waitlist/${id}`, { method: 'DELETE' }),
  },
};
